/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LogsModule
 */

import {
    AfterViewInit,
    Component,
    OnInit,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { L10nService } from '@vmw/ngx-vip';
import * as globalL10n from 'global-l10n';

import {
    IAviDataGridConfig,
} from 'ng/modules/data-grid/components/avi-data-grid/avi-data-grid.types';

import {
    IAviDiffGridConfig,
} from 'ng/modules/data-grid/components/avi-diff-grid/avi-diff-grid.types';

import * as l10n from './log-cinematic-sub-section-icap.l10n';
import { TrafficLogCinematicStore } from '../../../traffic-logs/traffic-log-cinematic/traffic-log-cinematic.store';

import './log-cinematic-sub-section-icap.component.less';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;
const { ...globalL10nKeys } = globalL10n;

/**
 * @description
 *     Component for displaying the ICAP sub-section in the Security section under traffic log cinematic view.
 * @author Zhiqian Liu
 */
@Component({
    selector: 'log-cinematic-sub-section-icap',
    templateUrl: './log-cinematic-sub-section-icap.component.html',
})
export class LogCinematicSubSectionIcapComponent implements OnInit, AfterViewInit {
    /**
     * Datagrid template for the Requestion Action field.
     */
    @ViewChild('requestActionTemplateRef')
    public readonly requestActionTemplateRef: TemplateRef<HTMLElement>;

    /**
     * Datagrid template for the Reason field.
     */
    @ViewChild('reasonTemplateRef')
    public readonly reasonTemplateRef: TemplateRef<HTMLElement>;

    /**
     * Datagrid template for the Violations field.
     */
    @ViewChild('violationsTemplateRef')
    public readonly violationsTemplateRef: TemplateRef<HTMLElement>;

    /**
     * Datagrid template for the expanded content of each ICAP request log.
     */
    @ViewChild('expandedContentTemplateRef')
    public readonly expandedContentTemplateRef: TemplateRef<HTMLElement>;

    public readonly icapLog$ = this.logCinematicStore.icapLog$;

    public readonly globalL10nKeys = globalL10nKeys;

    public readonly l10nKeys = l10nKeys;

    public icapRequestLogsGridConfig: IAviDataGridConfig;

    /**
     * Config for the diff grid showing ICAP "sent to server" and "received from server" headers.
     */
    public headersDiffGridConfig: IAviDiffGridConfig;

    constructor(
        public readonly logCinematicStore: TrafficLogCinematicStore,
        private readonly l10nService: L10nService,
    ) {
        this.l10nService.registerSourceBundles(dictionary);
    }

    /** @override */
    public ngOnInit(): void {
        this.icapRequestLogsGridConfig = {
            fields: [],
            layout: {
                hideCheckboxes: true,
            },
        };

        this.headersDiffGridConfig = {
            leftField: {
                label: this.l10nService.getMessage(globalL10nKeys.sentToServerLabel),
            },
            rightField: {
                label: this.l10nService.getMessage(globalL10nKeys.receivedFromServerLabel),
            },
            layout: {
                placeholderMessage:
                    this.l10nService.getMessage(globalL10nKeys.noHttpHeadersToShowPlaceholder),
            },
            isGridExpandable: true,
            noDiff: true,
        };
    }

    /** @override */
    public ngAfterViewInit(): void {
        this.icapRequestLogsGridConfig = {
            ...this.icapRequestLogsGridConfig,
            fields: [
                {
                    label: this.l10nService.getMessage(l10nKeys.requestActionLabel),
                    id: 'request-action',
                    templateRef: this.requestActionTemplateRef,
                },
                {
                    label: this.l10nService.getMessage(globalL10nKeys.reasonLabel),
                    id: 'reason',
                    templateRef: this.reasonTemplateRef,
                },
                {
                    label: this.l10nService.getMessage(l10nKeys.violationsLabel),
                    id: 'violations',
                    templateRef: this.violationsTemplateRef,
                },
            ],
            expandedContentTemplateRef: this.expandedContentTemplateRef,
        };
    }

    public trackByIndex(index: number): number {
        return index;
    }
}
